/* Progressive formatting only. Values and links remain supplied product data. */
(() => {
  'use strict';
  if (customElements.get('saber-specification-layout') && customElements.get('saber-product-resources')) return;
  const blocks = new Set(['P', 'DIV', 'UL', 'LI']);
  const allowed = new Set(['BR', ...blocks]);
  const hasLetters = value => /[A-Za-z\u00c0-\u024f\u0370-\u1fff]/.test(value);
  function plainLines(value) {
    for (const element of value.querySelectorAll('*')) {
      if (!allowed.has(element.tagName)) return null;
      for (const attribute of element.attributes) {
        if (attribute.name !== 'class' || element.tagName !== 'DIV' || attribute.value !== 'metafield-rich_text_field') return null;
      }
    }
    let text = '';
    function visit(node) {
      if (node.nodeType === 3) { text += node.nodeValue; return; }
      if (node.nodeType !== 1) return;
      if (node.tagName === 'BR') { text += '\n'; return; }
      if (blocks.has(node.tagName)) text += '\n';
      node.childNodes.forEach(visit);
      if (blocks.has(node.tagName)) text += '\n';
    }
    value.childNodes.forEach(visit);
    return text.replace(/\r\n?/g, '\n').split(/\n+/).map(line => line.trim()).filter(Boolean);
  }
  function pairsFrom(lines, heading) {
    if (!lines || lines.length < 2) return null;
    const pairs = [];
    let colonPairs = true;
    for (const line of lines) {
      const match = /^([^:]{1,90}):\s*(\S[\s\S]*)$/.exec(line);
      if (!match || !hasLetters(match[1]) || /\d\s*$/.test(match[1]) || /^(https?|ftp|file|blob|mailto|tel|sms|urn|data|javascript)$/i.test(match[1].trim())) { colonPairs = false; break; }
      pairs.push([match[1].trim(), match[2].trim()]);
    }
    if (colonPairs) return pairs;
    const normalizedHeading = String(heading || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    if (!/^(?:dimensions?|dimensiones|measurements?|medidas)$/i.test(normalizedHeading) || lines.length < 4 || lines.length % 2 !== 0) return null;
    const alternating = [];
    for (let index = 0; index < lines.length; index += 2) {
      const label = lines[index], value = lines[index + 1];
      const normalizedLabel = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (!/^(?:height|length|width|depth|diameter|thickness|alto|altura|largo|longitud|ancho|profundidad|diametro|grosor|espesor)(?:\s*\([^)]{1,40}\))?$/i.test(normalizedLabel) || !/^[-+]?(?:\d+(?:[.,]\d+)?|[.,]\d+)(?:\s*[^<>]{0,160})?$/.test(value)) return null;
      alternating.push([label, value]);
    }
    return alternating;
  }
  class SpecificationLayout extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.dataset.specificationReady === 'true') return;
        this.dataset.specificationReady = 'true';
        const grid = this.querySelector('.ss-pdp-specification-grid');
        if (!grid) return;
        for (const row of grid.children) {
          if (!row.matches('.ss-specification-row')) continue;
          const term = row.querySelector(':scope > dt'), value = row.querySelector(':scope > dd');
          if (!term || !value) continue;
          const lines = plainLines(value), pairs = pairsFrom(lines, term.textContent);
          if (!pairs) {
            if ((lines && lines.length >= 4) || value.textContent.trim().length > 300 || value.querySelectorAll('br,li,p').length >= 4) row.classList.add('ss-specification-row--long');
            continue;
          }
          const details = this.ownerDocument.createElement('dl');
          details.className = 'ss-specification-pairs';
          details.setAttribute('aria-label', term.textContent.trim());
          for (const [label, supplied] of pairs) {
            const pair = this.ownerDocument.createElement('div'), key = this.ownerDocument.createElement('dt'), item = this.ownerDocument.createElement('dd');
            pair.className = 'ss-specification-pair'; key.textContent = label; item.textContent = supplied;
            pair.append(key, item); details.append(pair);
          }
          value.replaceChildren(details);
          row.classList.add('ss-specification-row--structured');
        }
      });
    }
  }
  class ProductResources extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.dataset.resourcesReady === 'true') return;
        this.dataset.resourcesReady = 'true';
        const seen = new Set();
        for (const anchor of this.querySelectorAll('a[href]')) {
          const url = anchor.href;
          if (seen.has(url)) {
            const item = anchor.closest('li');
            const parent = anchor.parentElement;
            if (item && this.contains(item) && item.children.length === 1 && item.firstElementChild === anchor && item.textContent.trim() === anchor.textContent.trim()) { item.remove(); continue; }
            if (parent?.tagName === 'DD' && parent.children.length === 1 && parent.firstElementChild === anchor && parent.textContent.trim() === anchor.textContent.trim()) { anchor.remove(); continue; }
            // An inline/prose link can carry authored context; keep its text and
            // destination even if another resource uses the same URL.
          }
          seen.add(url);
          if (anchor.children.length) continue;
          const label = this.ownerDocument.createElement('span');
          label.textContent = anchor.textContent.trim().replace(/\s*[↗→]\s*$/, '').trim();
          const icon = this.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'svg'), path = this.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
          icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('width', '16'); icon.setAttribute('height', '16'); icon.setAttribute('fill', 'none'); icon.setAttribute('stroke', 'currentColor'); icon.setAttribute('stroke-width', '1.8'); icon.setAttribute('aria-hidden', 'true');
          path.setAttribute('d', anchor.target === '_blank' ? 'M8 5H5v14h14v-3M13 5h6v6M10 14l9-9' : 'M5 12h14m-5-5l5 5-5 5');
          icon.append(path); anchor.replaceChildren(label, icon);
        }
        for (const row of this.querySelectorAll('.ss-pdp-documents > .ss-specification-row')) {
          const value = row.querySelector(':scope > dd');
          if (value && !value.textContent.trim() && !value.querySelector('a,img')) row.remove();
        }
        for (const list of this.querySelectorAll('.ss-pdp-documents')) if (!list.children.length) list.hidden = true;
      });
    }
  }
  if (!customElements.get('saber-specification-layout')) customElements.define('saber-specification-layout', SpecificationLayout);
  if (!customElements.get('saber-product-resources')) customElements.define('saber-product-resources', ProductResources);
})();
